import React, { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import DishCard from "../components/DishCard";
import OrderDialog from "../components/OrderDialog";
import { Calendar, Loader2 } from "lucide-react";

const FORMATTER = new Intl.DateTimeFormat("it-IT", {
  weekday: "long",
  day: "numeric",
  month: "long",
});

function dateLabel(dateStr) {
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);
  const tomorrow = new Date(today.getTime() + 86400000);
  const tomorrowStr = tomorrow.toISOString().slice(0, 10);
  if (dateStr === todayStr) return "Oggi";
  if (dateStr === tomorrowStr) return "Domani";
  const [y, m, d] = dateStr.split("-").map(Number);
  return FORMATTER.format(new Date(y, m - 1, d));
}

export default function Menu() {
  const [dishes, setDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchDishes = async () => {
    try {
      const { data } = await api.get("/dishes/today");
      setDishes(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDishes();
    const t = setInterval(fetchDishes, 30000);
    return () => clearInterval(t);
  }, []);

  const groups = useMemo(() => {
    const map = new Map();
    for (const d of dishes) {
      if (!map.has(d.date)) map.set(d.date, []);
      map.get(d.date).push(d);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [dishes]);

  const onBook = (dish) => {
    setSelected(dish);
    setDialogOpen(true);
  };

  const today = FORMATTER.format(new Date());

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-muted-foreground uppercase tracking-widest font-medium mb-2">
          <Calendar className="w-4 h-4" />
          <span data-testid="today-date">{today}</span>
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Menu
        </h1>
        <p className="text-muted-foreground mt-2">
          Prenotazioni aperte. Le porzioni sono limitate.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento…
        </div>
      ) : dishes.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center" data-testid="menu-empty">
          <div className="text-4xl mb-3">🍽</div>
          <p className="font-display text-lg font-medium mb-1">Nessun piatto prenotabile</p>
          <p className="text-sm text-muted-foreground">
            Torna più tardi o chiedi ai cuochi di pubblicare qualcosa.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {groups.map(([date, items]) => (
            <section key={date} data-testid={`menu-group-${date}`}>
              <div className="flex items-baseline justify-between mb-4">
                <h2 className="font-display text-xl sm:text-2xl font-semibold tracking-tight">
                  {dateLabel(date)}
                </h2>
                <span className="text-xs text-muted-foreground">{items.length} piatto/i</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
                {items.map((dish) => (
                  <DishCard key={dish.id} dish={dish} onBook={onBook} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <OrderDialog
        dish={selected}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={fetchDishes}
      />
    </div>
  );
}
